import { useSelector } from 'react-redux';

import { HostPlatform } from 'store/host-info/consts';
import {
  selectHostPlatform,
  selectIsHostInfoSettled,
  selectIsNativeAppHost,
  selectIsTenantPortalHost,
} from 'store/host-info/selectors';

interface UseHostInfoReturnValues {
  /** The host's package name, or `null` until it answers. */
  hostPlatform: HostPlatform | null;
  /** The tenant portal is framing this app, and draws its own back affordance around the frame. */
  isTenantPortalHost: boolean;
  /**
   * The native app is framing this app, and draws its own HEADER above the frame -- so these pages
   * hand it the title through `setHeader` and draw no header of their own.
   *
   * ⚠️ `false` until the host answers, which is the safe way round: the fallback draws our own way
   * out rather than deferring to a header that may not exist. See `selectIsNativeAppHost`.
   */
  isNativeAppHost: boolean;
  /** The host has answered. Read this to avoid committing to chrome before the answer lands. */
  isHostInfoSettled: boolean;
}

/**
 * What is framing this app.
 *
 * Reads the answer `useSyncHostInfoWithHostApp` recorded at app mount; it never asks on its own, so
 * it is safe to call from anywhere, as many times as needed.
 */
export const useHostInfo = (): UseHostInfoReturnValues => ({
  hostPlatform: useSelector(selectHostPlatform),
  isTenantPortalHost: useSelector(selectIsTenantPortalHost),
  isNativeAppHost: useSelector(selectIsNativeAppHost),
  isHostInfoSettled: useSelector(selectIsHostInfoSettled),
});
